import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react'
import { getPrfSecret, enrollAndGetSecret, PRF_SALT_HYPERLIQUID } from '../../utils/webauthnPrf'
import { remember, restore, forget } from '../../utils/unlockCache'

const STORAGE_KEY = 'solcheddar_hl_keys'
// Biometric envelope — additive, same rules as the Solana wallet: the
// password blob is never touched, and this one is derived from the passkey at
// a DIFFERENT PRF salt, so the two wallets stay cryptographically independent
// even though one finger opens both.
const BIO_KEY = STORAGE_KEY + '_bio'
const HyperliquidWalletContext = createContext(null)

function formatMaskedAddress(address, prefixLength = 3, suffixLength = 4) {
  const value = String(address || '').trim()
  if (!value) return ''
  if (value.length <= prefixLength + suffixLength) return value
  return `${value.slice(0, prefixLength)}...${value.slice(-suffixLength)}`.replace(/^0x/, '0x')
}

// ─── AES-GCM helpers (same pattern as WalletContext.jsx) ───

async function deriveKey(password) {
  const enc = new TextEncoder().encode(password)
  const hash = await crypto.subtle.digest('SHA-256', enc)
  return crypto.subtle.importKey('raw', hash, 'AES-GCM', false, ['encrypt', 'decrypt'])
}

async function encrypt(text, password) {
  const key = await deriveKey(password)
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, new TextEncoder().encode(text))
  return JSON.stringify({
    iv: Array.from(iv),
    ct: Array.from(new Uint8Array(ct)),
  })
}

async function decrypt(stored, password) {
  const { iv, ct } = JSON.parse(stored)
  const key = await deriveKey(password)
  const pt = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: new Uint8Array(iv) },
    key,
    new Uint8Array(ct),
  )
  return new TextDecoder().decode(pt)
}

export function HyperliquidWalletProvider({ children }) {
  const [maskedAddress, setMaskedAddress] = useState('')
  // Public address (full, unmasked) — exposed so consumers like
  // usePriceAlerts can use it as the alert-owner identity. Distinct from
  // the privateKey (which never leaves keysRef).
  const [accountAddress, setAccountAddress] = useState('')
  const [isLocked, setIsLocked] = useState(true)
  const [hasStoredKeys, setHasStoredKeys] = useState(false)
  const [hasBiometric, setHasBiometric] = useState(false)
  const wsRef = useRef(null)

  // Internal: decrypted keys (never in state — only in ref)
  const keysRef = useRef(null) // { privateKey, accountAddress }

  // Check if keys exist in localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    setHasStoredKeys(!!stored)
    setHasBiometric(!!localStorage.getItem(BIO_KEY))
    if (stored) {
      try {
        // Extract masked address from metadata
        const meta = JSON.parse(localStorage.getItem(STORAGE_KEY + '_meta') || '{}')
        if (meta.maskedAddress) setMaskedAddress(meta.maskedAddress)
      } catch {}
    }
    // 12h remember: restore the unlocked keys after a new tab / page load.
    // hl_auth + set_wallet are sent by setWsRef / the auth heartbeat once
    // isLocked flips false and a socket is open.
    if (!stored) { forget('hl'); return }
    let cancelled = false
    restore('hl').then(raw => {
      if (cancelled || !raw || keysRef.current) return
      try {
        const keys = JSON.parse(raw)
        if (!keys || !keys.privateKey || !keys.accountAddress) throw new Error('bad')
        keysRef.current = keys
        setMaskedAddress(formatMaskedAddress(keys.accountAddress))
        setAccountAddress(keys.accountAddress)
        setIsLocked(false)
      } catch { forget('hl') }
    })
    return () => { cancelled = true }
  }, [])

  // Send auth to server when keys are available
  const sendAuth = useCallback(() => {
    const ws = wsRef.current
    const keys = keysRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN || !keys) return
    ws.send(JSON.stringify({
      action: 'hl_auth',
      privateKey: keys.privateKey,
      accountAddress: keys.accountAddress,
    }))
  }, [])

  // Register the HL address as this connection's alert-owner. Server uses
  // cs.wallet as the owner key for price alerts; without this, add_alert
  // is rejected with "set wallet before adding alerts". Sent alongside
  // hl_auth on unlock and on every WS reconnect.
  const sendSetWallet = useCallback((addr) => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    try {
      ws.send(JSON.stringify({ action: 'set_wallet', wallet: addr || null }))
    } catch {}
  }, [])

  const importKeys = useCallback(async (privateKey, accountAddress, password) => {
    const payload = JSON.stringify({ privateKey, accountAddress })
    const encrypted = await encrypt(payload, password)
    localStorage.setItem(STORAGE_KEY, encrypted)
    remember('hl', payload)

    const masked = formatMaskedAddress(accountAddress)
    localStorage.setItem(STORAGE_KEY + '_meta', JSON.stringify({ maskedAddress: masked }))

    keysRef.current = { privateKey, accountAddress }
    setMaskedAddress(masked)
    setAccountAddress(accountAddress)
    setIsLocked(false)
    setHasStoredKeys(true)

    sendAuth()
    sendSetWallet(accountAddress)
  }, [sendAuth, sendSetWallet])

  const unlock = useCallback(async (password) => {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) throw new Error('No stored keys')

    const raw = await decrypt(stored, password)
    const keys = JSON.parse(raw)
    remember('hl', raw)
    keysRef.current = keys

    const masked = formatMaskedAddress(keys.accountAddress)
    setMaskedAddress(masked)
    setAccountAddress(keys.accountAddress)
    setIsLocked(false)

    sendAuth()
    sendSetWallet(keys.accountAddress)
  }, [sendAuth, sendSetWallet])

  const lock = useCallback(() => {
    keysRef.current = null
    forget('hl')
    setAccountAddress('')
    setIsLocked(true)
    // Clear server-side alert ownership so a different user on the same
    // browser session doesn't inherit access to our alerts.
    sendSetWallet(null)
  }, [sendSetWallet])

  const removeKeys = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY)
    localStorage.removeItem(STORAGE_KEY + '_meta')
    localStorage.removeItem(BIO_KEY)   // never outlive the keys it unlocks
    forget('hl')
    keysRef.current = null
    setMaskedAddress('')
    setAccountAddress('')
    setIsLocked(true)
    setHasStoredKeys(false)
    setHasBiometric(false)
    sendSetWallet(null)
  }, [sendSetWallet])

  // ─── Biometric unlock (WebAuthn PRF) ───
  // Enroll from the UNLOCKED state: keysRef already holds the payload, so no
  // password re-prompt is needed. Encrypts the same {privateKey,
  // accountAddress} JSON with the PRF-derived secret.
  const enableBiometric = useCallback(async () => {
    const k = keysRef.current
    if (!k) throw new Error('Connect your keys first')
    const secret = await enrollAndGetSecret(PRF_SALT_HYPERLIQUID, 'Cheddar Hyperliquid keys')
    localStorage.setItem(BIO_KEY, await encrypt(JSON.stringify(k), secret))
    setHasBiometric(true)
  }, [])

  // Must land in the SAME end state as unlock(password) — including the two
  // session frames. Skipping sendAuth/sendSetWallet here would connect the
  // UI while leaving the socket unauthenticated: hl_poll returns empty,
  // balance reads 0, position tags vanish (the per-connection auth trap
  // documented in setWsRef).
  const unlockWithBiometric = useCallback(async () => {
    const stored = localStorage.getItem(BIO_KEY)
    if (!stored) throw new Error('Fingerprint unlock is not set up')
    const secret = await getPrfSecret(PRF_SALT_HYPERLIQUID)
    let keys
    try {
      keys = JSON.parse(await decrypt(stored, secret))
    } catch {
      localStorage.removeItem(BIO_KEY)
      setHasBiometric(false)
      throw new Error('Fingerprint no longer matches these keys — use your password')
    }
    remember('hl', JSON.stringify(keys))
    keysRef.current = keys
    setMaskedAddress(formatMaskedAddress(keys.accountAddress))
    setAccountAddress(keys.accountAddress)
    setIsLocked(false)
    sendAuth()
    sendSetWallet(keys.accountAddress)
  }, [sendAuth, sendSetWallet])

  const disableBiometric = useCallback(() => {
    localStorage.removeItem(BIO_KEY)
    setHasBiometric(false)
  }, [])

  // Send both session frames on a specific socket. One place, so the
  // three callers (setWsRef, its open-arm, the heartbeat) cannot drift.
  const authOn = useCallback((sock) => {
    const k = keysRef.current
    if (!sock || sock.readyState !== WebSocket.OPEN || !k) return
    try {
      sock.send(JSON.stringify({ action: 'hl_auth', privateKey: k.privateKey, accountAddress: k.accountAddress }))
      sock.send(JSON.stringify({ action: 'set_wallet', wallet: k.accountAddress }))
    } catch {}
  }, [])

  // Sockets we've already attached an open-listener to, so repeated
  // setWsRef calls for the same CONNECTING socket don't stack listeners.
  const armedRef = useRef(null)

  const setWsRef = useCallback((ref) => {
    const prev = wsRef.current
    wsRef.current = ref
    if (!ref || !keysRef.current) return
    // Server ClientState.hl_keys (and cs.wallet) are PER-CONNECTION, so a
    // new socket MUST be re-authed or hl_poll returns empty: balance 0,
    // position tags gone.
    //
    // THE RACE this closes: the caller's effect depends on the context
    // value, which is an unmemoized object literal — so it re-runs on
    // EVERY provider render, not just on status changes. When a render
    // landed between `wsRef.current = ws` (still CONNECTING) and its
    // onopen, this function stored the new socket but the OPEN guard
    // blocked the auth; then onopen flipped status, the effect re-ran,
    // and `ref !== prev` was now false — same instance — so the auth was
    // skipped a second time. The connection ended up authed by nobody,
    // and stayed that way until a manual lock/unlock forced it.
    //
    // Binding to the socket's own 'open' event removes effect timing from
    // the correctness path entirely. addEventListener, NOT onopen: the
    // chart hook owns ws.onopen and assigning it would silently kill
    // resubscribe-on-reconnect.
    if (ref.readyState === WebSocket.OPEN) {
      if (ref !== prev) authOn(ref)
      return
    }
    if (ref.readyState === WebSocket.CONNECTING && armedRef.current !== ref) {
      armedRef.current = ref
      ref.addEventListener('open', () => authOn(ref), { once: true })
    }
  }, [authOn])

  // ─── Auth heartbeat ───
  // Server-side hl_keys are PER-CONNECTION (the comment above documents the
  // failure: any reconnect without a fresh hl_auth makes hl_poll return
  // empty and the balance shows 0). setWsRef re-auths only when handed a
  // DIFFERENT, already-OPEN socket — correctness hostage to caller wiring
  // and open-timing races, and this session has churn to spare (fills
  // timeouts, watchdog bounces). This heartbeat makes auth SELF-HEALING:
  // re-send hl_auth + set_wallet on the open socket every 20s and
  // immediately on wake. Idempotent server-side — it just re-sets two
  // fields — so the cost is two tiny frames per interval, and no
  // reconnect path can ever leave a connection unauthenticated for more
  // than one beat. Gated on unlocked: locking stops the heartbeat cold.
  useEffect(() => {
    if (isLocked) return
    const reauth = () => authOn(wsRef.current)
    reauth()   // heal immediately on unlock as well
    const iv = setInterval(reauth, 20_000)
    const onWake = () => { if (document.visibilityState === 'visible') reauth() }
    document.addEventListener('visibilitychange', onWake)
    window.addEventListener('pageshow', onWake)
    return () => {
      clearInterval(iv)
      document.removeEventListener('visibilitychange', onWake)
      window.removeEventListener('pageshow', onWake)
    }
  }, [isLocked, authOn])

  const isReady = !isLocked && hasStoredKeys && !!keysRef.current

  return (
    <HyperliquidWalletContext.Provider value={{
      maskedAddress,
      accountAddress,        // public address (e.g. for alert ownership)
      isLocked,
      hasStoredKeys,
      isReady,
      importKeys,
      unlock,
      lock,
      removeKeys,
      setWsRef,
      hasBiometric,
      enableBiometric,
      unlockWithBiometric,
      disableBiometric,
    }}>
      {children}
    </HyperliquidWalletContext.Provider>
  )
}

export function useHyperliquidWallet() {
  const ctx = useContext(HyperliquidWalletContext)
  if (!ctx) throw new Error('useHyperliquidWallet must be used inside HyperliquidWalletProvider')
  return ctx
}